import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { contributionAPI, fileAPI, ensureFreshAccessToken } from '../services/api'
import FileUpload from './FileUpload'
import {
  PRESET_DEVICE_TYPES,
  CUSTOM_DEVICE_TYPE_VALUE,
  splitDeviceTypeForForm,
  resolveDeviceTypeForMetadata,
} from '../utils/deviceType'
import './ContributionForm.css'

function extractFileId(f) {
  if (f == null) return null
  if (typeof f === 'string') return f
  if (typeof f === 'object' && f.id != null) return String(f.id)
  return null
}

function isLocalFileEntry(f) {
  return Boolean(f && f.local === true && f.file instanceof File)
}

function normalizeLoadedFileRef(raw) {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const id = raw
    return { id, local: false, name: id.length > 12 ? `${id.slice(0, 8)}…` : id }
  }
  if (typeof raw === 'object' && raw.id != null) {
    const id = String(raw.id)
    return {
      ...raw,
      id,
      local: false,
      name: raw.original_filename ?? raw.name ?? (id.length > 12 ? `${id.slice(0, 8)}…` : id),
    }
  }
  return null
}

function descriptionKeyForEntry(entry) {
  if (entry == null) return null
  if (isLocalFileEntry(entry)) return entry.tempId || entry.name
  if (typeof entry === 'string') return entry
  if (entry.id != null) return String(entry.id)
  return null
}

async function resolveDeferredFiles(entries, options = {}) {
  const circuitNotes = options.circuitNotes === true
  const sourceFiles = options.sourceFiles === true
  const resolved = []
  for (const item of entries || []) {
    if (isLocalFileEntry(item)) {
      const response = await fileAPI.upload(item.file, undefined, { circuitNotes, sourceFiles })
      resolved.push(response.file)
    } else if (typeof item === 'string') {
      resolved.push({ id: item })
    } else if (item && item.id != null) {
      resolved.push(item)
    }
  }
  return resolved
}

function remapDescriptionsAfterUpload(originalEntries, resolvedEntries, descriptions) {
  const out = { ...(descriptions || {}) }
  for (let i = 0; i < originalEntries.length; i++) {
    const oldKey = descriptionKeyForEntry(originalEntries[i])
    const newId = extractFileId(resolvedEntries[i])
    if (oldKey && newId && out[oldKey] != null && out[oldKey] !== '') {
      out[newId] = out[oldKey]
      if (oldKey !== newId) delete out[oldKey]
    }
  }
  return out
}

function ContributionForm({ contributionId, initialData, mode = 'contribute', onSuccess, onCancel, repositoryOwnerId, canManageRepository = false }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const ownerId = repositoryOwnerId ?? initialData?.repository_owner_id
  const isRepoOwner = Boolean(
    canManageRepository || (user?.id && ownerId && String(user.id) === String(ownerId))
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploadedNoteFiles, setUploadedNoteFiles] = useState([]) // Markdown for notes/ (new_commit only)
  const [uploadedSourceFiles, setUploadedSourceFiles] = useState([])
  const [sourceFileDescriptions, setSourceFileDescriptions] = useState({})
  const [hasLegalAcknowledgment, setHasLegalAcknowledgment] = useState(false)
  const [showBranchForkConfirm, setShowBranchForkConfirm] = useState(false)
  const [hasAcknowledgedBranchFork, setHasAcknowledgedBranchFork] = useState(false)
  const [customDeviceType, setCustomDeviceType] = useState('')

  const isNewRepoMode = (m, cId, data) => {
    if (cId) return null // will be set after load
    if (m === 'new_repository') return true
    if (data?.submission_type === 'new_repository') return true
    return false
  }

  const [formData, setFormData] = useState({
    submission_type: initialData?.submission_type || (mode === 'new_repository' ? 'new_repository' : 'new_commit'),
    repository_id: initialData?.repository_id ?? (mode === 'new_repository' ? null : ''),
    status: 'draft',
    metadata: {
      title: '',
      description: '',
      manufacturer: '',
      model_number: '',
      device_type: '',
      form_factor: '',
      year: '',
      notes: '',
      commit_intent: 'add',
      notes_intent: 'add', // circuit_notes: 'add' = add to existing notes; 'replace' = replace entire notes/
      // Optional provenance metadata (how this schematic/notes were produced)
      production_method: '',
      tools_used: '',
      confidence_level: '',
      known_differences: '',
    },
  })

  const [isNewRepo, setIsNewRepo] = useState(isNewRepoMode(mode, contributionId, initialData))

  const loadContribution = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await contributionAPI.get(contributionId)
      const contribution = response.contribution

      const st = contribution.submission_type
      const submissionType = (st === 'new_commit' || st === 'new_issue' || st === 'circuit_notes' || st === 'new_repository') ? st : 'new_commit'
      const { select: deviceTypeSelect, custom: deviceTypeCustom } = splitDeviceTypeForForm(
        contribution.metadata?.device_type
      )
      setCustomDeviceType(deviceTypeCustom)
      setIsNewRepo(st === 'new_repository')
      setFormData({
        submission_type: submissionType,
        repository_id: contribution.repository_id || (st === 'new_repository' ? null : ''),
        status: contribution.status,
        metadata: {
          ...contribution.metadata,
          title: contribution.metadata?.title || '',
          description: contribution.metadata?.description || '',
          manufacturer: contribution.metadata?.manufacturer || '',
          model_number: contribution.metadata?.model_number || '',
          device_type: deviceTypeSelect,
          form_factor: contribution.metadata?.form_factor || '',
          year: contribution.metadata?.year || '',
          notes: contribution.metadata?.notes || '',
          commit_intent: contribution.metadata?.commit_intent || 'add',
          notes_intent: contribution.metadata?.notes_intent || 'add',
          production_method: contribution.metadata?.production_method || '',
          tools_used: contribution.metadata?.tools_used || '',
          confidence_level: contribution.metadata?.confidence_level || '',
          known_differences: contribution.metadata?.known_differences || '',
        },
      })

      setUploadedFiles(
        Array.isArray(contribution.metadata?.file_ids)
          ? contribution.metadata.file_ids.map(normalizeLoadedFileRef).filter(Boolean)
          : []
      )
      setUploadedNoteFiles(
        Array.isArray(contribution.metadata?.note_file_ids)
          ? contribution.metadata.note_file_ids.map(normalizeLoadedFileRef).filter(Boolean)
          : []
      )
      setUploadedSourceFiles(
        Array.isArray(contribution.metadata?.source_file_ids)
          ? contribution.metadata.source_file_ids.map(normalizeLoadedFileRef).filter(Boolean)
          : []
      )
      const loadedDescriptions = contribution.metadata?.source_file_descriptions
      setSourceFileDescriptions(
        loadedDescriptions && typeof loadedDescriptions === 'object' ? { ...loadedDescriptions } : {}
      )
    } catch (error) {
      setError(error.message || 'Failed to load contribution')
    } finally {
      setIsLoading(false)
    }
  }, [contributionId])

  // Load existing contribution if editing, or use initialData / mode
  useEffect(() => {
    if (contributionId) {
      loadContribution()
    } else if (initialData) {
      setFormData(prev => ({
        ...prev,
        submission_type: initialData.submission_type || prev.submission_type,
        repository_id: initialData.repository_id ?? prev.repository_id,
      }))
      setIsNewRepo(initialData.submission_type === 'new_repository')
    } else if (mode === 'new_repository') {
      setFormData(prev => ({ ...prev, submission_type: 'new_repository', repository_id: null }))
      setIsNewRepo(true)
    }
  }, [contributionId, initialData, mode, loadContribution])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith('metadata.')) {
      const metadataKey = name.replace('metadata.', '')
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataKey]: value,
        },
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleDeviceTypeChange = (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        device_type: value,
      },
    }))
    if (value !== CUSTOM_DEVICE_TYPE_VALUE) {
      setCustomDeviceType('')
    }
  }

  const getResolvedDeviceType = () => resolveDeviceTypeForMetadata(
    formData.metadata?.device_type,
    customDeviceType
  )

  const buildNewRepoMetadata = (baseMeta) => {
    const meta = { ...baseMeta }
    if (isNewRepo) {
      const resolvedDeviceType = getResolvedDeviceType()
      if (resolvedDeviceType) {
        meta.device_type = resolvedDeviceType
      } else {
        delete meta.device_type
      }
      if (meta.year) {
        const y = parseInt(meta.year, 10)
        if (!Number.isNaN(y)) meta.year = y
        else delete meta.year
      }
    }
    return meta
  }

  const handleFilesChange = (files) => {
    setUploadedFiles(files)
    const fileIds = files
      .filter((f) => !isLocalFileEntry(f))
      .map((f) => extractFileId(f))
      .filter(Boolean)
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        file_ids: fileIds,
      },
    }))
  }

  const handleNoteFilesChange = (files) => {
    setUploadedNoteFiles(files)
    const noteIds = files
      .filter((f) => !isLocalFileEntry(f))
      .map((f) => extractFileId(f))
      .filter(Boolean)
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        note_file_ids: noteIds,
      },
    }))
  }

  const handleSourceFilesChange = (files) => {
    setUploadedSourceFiles(files)
    const sourceIds = files
      .filter((f) => !isLocalFileEntry(f))
      .map((f) => extractFileId(f))
      .filter(Boolean)
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        source_file_ids: sourceIds,
      },
    }))
  }

  const handleSourceDescriptionChange = (fileKey, description) => {
    setSourceFileDescriptions((prev) => ({
      ...prev,
      [fileKey]: description,
    }))
  }

  const isNotesOnly = formData.submission_type === 'circuit_notes'
  const isPendingBranchUpdate = formData.status === 'pending' && (formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes')
  const canEdit = formData.status === 'draft' || isPendingBranchUpdate
  const deferFileUpload = formData.status === 'draft'
  const schematicServerFiles = useMemo(
    () => uploadedFiles.filter((f) => !isLocalFileEntry(f)),
    [uploadedFiles]
  )
  const noteServerFiles = useMemo(
    () => (uploadedNoteFiles || []).filter((f) => !isLocalFileEntry(f)),
    [uploadedNoteFiles]
  )
  const sourceServerFiles = useMemo(
    () => (uploadedSourceFiles || []).filter((f) => !isLocalFileEntry(f)),
    [uploadedSourceFiles]
  )

  const validateForm = (forSubmit = false) => {
    if (isNewRepo) {
      const title = (formData.metadata?.title || '').trim()
      if (!title) return 'Title is required for new repositories.'
      const description = (formData.metadata?.description || '').trim()
      if (!description) return 'Description is required for new repositories.'
      const mfr = (formData.metadata?.manufacturer || '').trim()
      const model = (formData.metadata?.model_number || '').trim()
      const dtype = getResolvedDeviceType()
      if (formData.metadata?.device_type === CUSTOM_DEVICE_TYPE_VALUE && !dtype) {
        return 'Enter a custom device type or choose a different option.'
      }
      if (!mfr && !model && !dtype) return 'At least one of Manufacturer, Model number, or Device type is required for new repositories.'
      if (forSubmit) {
        const hasSchematics = uploadedFiles && uploadedFiles.length > 0
        const hasNotes = uploadedNoteFiles && uploadedNoteFiles.length > 0
        if (!hasSchematics && !hasNotes) {
          return 'Add at least one schematic file or one circuit notes file (.md) when submitting a new repository. Circuit-notes-only repos are allowed.'
        }
      }
      return null
    }

    if (!formData.repository_id) return 'Repository is required'

      if (forSubmit && isNotesOnly) {
        if (!uploadedFiles || uploadedFiles.length === 0) {
          return 'At least one notes file is required for circuit notes. Add Markdown (.md) file(s) to add or replace in the notes/ file structure.'
        }
      const notes = (formData.metadata?.notes || '').trim()
      if (!notes) return 'Commit message (notes) is required when submitting circuit notes.'
    }

    if (formData.submission_type === 'new_commit') {
      const hasSchematics = uploadedFiles && uploadedFiles.length > 0
      const hasSource = uploadedSourceFiles && uploadedSourceFiles.length > 0
      const hasNotes = uploadedNoteFiles && uploadedNoteFiles.length > 0
      if (!hasSchematics && !hasSource && !hasNotes) {
        return 'Add at least one schematic, source, or circuit notes file for a new commit.'
      }
      if (forSubmit) {
        const notes = (formData.metadata?.notes || '').trim()
        if (!notes) return 'Commit message (notes) is required when submitting a new commit.'
      }
    }
    if (forSubmit && formData.submission_type === 'new_issue') {
      const notes = (formData.metadata?.notes || '').trim()
      if (!notes) return 'Issue description is required before submitting.'
    }

    if (
      forSubmit &&
      (formData.submission_type === 'new_commit' ||
        formData.submission_type === 'circuit_notes' ||
        isNewRepo)
    ) {
      if (!hasLegalAcknowledgment) {
        return 'You must confirm that this submission is your original work or derived from lawful reverse engineering and does not include scans, photos, or transcriptions of copyrighted manuals or traced proprietary schematics.'
      }
    }

    return null
  }

  const handleSaveDraft = async () => {
    const validationError = validateForm(false)
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setIsSaving(true)
      setError('')

      const schematicIds = (isNewRepo || formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes')
        ? uploadedFiles.filter((f) => !isLocalFileEntry(f)).map((f) => extractFileId(f)).filter(Boolean)
        : []
      const noteFileIds = (formData.submission_type === 'new_commit' || isNewRepo)
        ? (uploadedNoteFiles || []).filter((f) => !isLocalFileEntry(f)).map((f) => extractFileId(f)).filter(Boolean)
        : []
      const sourceFileIds = (formData.submission_type === 'new_commit' || isNewRepo)
        ? (uploadedSourceFiles || []).filter((f) => !isLocalFileEntry(f)).map((f) => extractFileId(f)).filter(Boolean)
        : []
      const fileIds = schematicIds
      const meta = buildNewRepoMetadata({
        ...formData.metadata,
        file_ids: fileIds,
        note_file_ids: noteFileIds,
        source_file_ids: sourceFileIds,
        source_file_descriptions: sourceFileDescriptions,
      })
      const contributionData = {
        submission_type: formData.submission_type,
        status: 'draft',
        metadata: meta,
      }

      if (!isNewRepo && formData.repository_id) {
        try {
          contributionData.repository_id = formData.repository_id
        } catch (e) {
          contributionData.repository_id = formData.repository_id
        }
      }

      if (contributionId) {
        await contributionAPI.update(contributionId, contributionData)
      } else {
        await contributionAPI.create(contributionData)
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      setError(error.message || 'Failed to save draft')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async () => {
    const validationError = validateForm(true)
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setIsLoading(true)
      setError('')

      let schematicForMeta = uploadedFiles
      let notesForMeta = uploadedNoteFiles || []
      let sourceForMeta = uploadedSourceFiles || []
      let descriptionsForMeta = sourceFileDescriptions
      if (formData.status === 'draft') {
        try {
          await ensureFreshAccessToken()
          schematicForMeta = await resolveDeferredFiles(uploadedFiles, {
            circuitNotes: formData.submission_type === 'circuit_notes',
          })
          if (formData.submission_type === 'new_commit' || isNewRepo) {
            notesForMeta = await resolveDeferredFiles(uploadedNoteFiles || [], { circuitNotes: true })
            sourceForMeta = await resolveDeferredFiles(uploadedSourceFiles || [], { sourceFiles: true })
            descriptionsForMeta = remapDescriptionsAfterUpload(
              uploadedSourceFiles || [],
              sourceForMeta,
              sourceFileDescriptions
            )
          }
        } catch (uploadErr) {
          setError(uploadErr.message || 'File upload failed')
          setIsLoading(false)
          return
        }
      }

      const schematicIdsSubmit = (isNewRepo || formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes')
        ? schematicForMeta.map((f) => extractFileId(f)).filter(Boolean)
        : []
      const noteFileIdsSubmit = (formData.submission_type === 'new_commit' || isNewRepo)
        ? notesForMeta.map((f) => extractFileId(f)).filter(Boolean)
        : []
      const sourceFileIdsSubmit = (formData.submission_type === 'new_commit' || isNewRepo)
        ? sourceForMeta.map((f) => extractFileId(f)).filter(Boolean)
        : []

      const descriptionsByFileId = {}
      for (const id of sourceFileIdsSubmit) {
        const desc = descriptionsForMeta[id]
        if (desc && String(desc).trim()) {
          descriptionsByFileId[id] = String(desc).trim()
        }
      }

      const metaPending = buildNewRepoMetadata({
        ...formData.metadata,
        file_ids: schematicIdsSubmit,
        note_file_ids: noteFileIdsSubmit,
        source_file_ids: sourceFileIdsSubmit,
        source_file_descriptions: descriptionsByFileId,
      })
      const contributionData = {
        submission_type: formData.submission_type,
        status: 'pending',
        metadata: metaPending,
      }

      if (!isNewRepo && formData.repository_id) {
        contributionData.repository_id = formData.repository_id
      }

      if (contributionId) {
        await contributionAPI.update(contributionId, contributionData)
      } else {
        await contributionAPI.create(contributionData)
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      setError(error.message || 'Failed to submit contribution')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitWithBranchForkConfirm = () => {
    const needsBranchForkConfirm =
      formData.status === 'draft' &&
      (formData.submission_type === 'new_commit' ||
        formData.submission_type === 'circuit_notes' ||
        isNewRepo)

    if (needsBranchForkConfirm && !hasAcknowledgedBranchFork) {
      setShowBranchForkConfirm(true)
      return
    }

    handleSubmit()
  }

  if (isLoading && contributionId) {
    return <div className="contribution-form-loading">Loading...</div>
  }

  const deviceTypes = PRESET_DEVICE_TYPES
  const formFactors = ['pedal', 'rack', 'desktop', 'modular', 'console', 'other']

  return (
    <div className="contribution-form">
      <h2 className="contribution-form-title">
        {isPendingBranchUpdate ? 'Update branch' : contributionId ? 'Edit Contribution' : (isNewRepo ? 'Create new repository' : 'New contribution')}
      </h2>

      {(formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes' || isNewRepo) && (
        <div
          className="contribution-form-submit-note contribution-form-branch-warning"
          role="note"
          aria-label="How branches and forks work"
          style={{
            marginBottom: '0.75rem',
            fontSize: '0.8rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          <div className="contribution-form-branch-warning-title">how commits work</div>
          <strong>Branches are temporary, main is canonical.</strong>{' '}
          Your submission creates a <strong>contribution branch</strong> and <strong>pull request</strong> that targets the
          repository&apos;s <strong>main</strong> branch. Repo owners/admins decide what lands in main. Forking is available as an alternative to commiting to this repos Main branch
        </div>
      )}

      {error && (
        <div className="contribution-form-error">
          {error}
        </div>
      )}

      {isNewRepo ? (
        <>
          <div className="contribution-form-section">
            <label className="form-group">
              <span className="form-label">Title *</span>
              <input
                type="text"
                name="metadata.title"
                value={formData.metadata.title}
                onChange={handleInputChange}
                placeholder="Repository title (e.g. Moog Mother-32)"
                className="form-input"
                disabled={contributionId && !canEdit}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Description *</span>
              <textarea
                name="metadata.description"
                value={formData.metadata.description}
                onChange={handleInputChange}
                placeholder="Brief description of the circuit"
                className="form-input form-textarea"
                rows="2"
                disabled={contributionId && !canEdit}
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <label className="form-group">
                <span className="form-label">Manufacturer</span>
                <input
                  type="text"
                  name="metadata.manufacturer"
                  value={formData.metadata.manufacturer}
                  onChange={handleInputChange}
                  placeholder="e.g. Moog"
                  className="form-input"
                  disabled={contributionId && !canEdit}
                />
              </label>
              <label className="form-group">
                <span className="form-label">Model number</span>
                <input
                  type="text"
                  name="metadata.model_number"
                  value={formData.metadata.model_number}
                  onChange={handleInputChange}
                  placeholder="e.g. Mother-32"
                  className="form-input"
                  disabled={contributionId && !canEdit}
                />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <label className="form-group">
                <span className="form-label">Device type</span>
                <select
                  name="metadata.device_type"
                  value={formData.metadata.device_type}
                  onChange={handleDeviceTypeChange}
                  className="form-input"
                  disabled={contributionId && !canEdit}
                >
                  <option value="">—</option>
                  {deviceTypes.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                  <option value={CUSTOM_DEVICE_TYPE_VALUE}>Custom…</option>
                </select>
                {formData.metadata.device_type === CUSTOM_DEVICE_TYPE_VALUE && (
                  <input
                    type="text"
                    value={customDeviceType}
                    onChange={(e) => setCustomDeviceType(e.target.value)}
                    placeholder="e.g. drum machine, preamp"
                    className="form-input"
                    style={{ marginTop: '0.5rem' }}
                    disabled={contributionId && !canEdit}
                  />
                )}
              </label>
              <label className="form-group">
                <span className="form-label">Form factor</span>
                <select
                  name="metadata.form_factor"
                  value={formData.metadata.form_factor}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={contributionId && !canEdit}
                >
                  <option value="">—</option>
                  {formFactors.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </label>
              <label className="form-group">
                <span className="form-label">Year</span>
                <input
                  type="number"
                  name="metadata.year"
                  value={formData.metadata.year ?? ''}
                  onChange={(e) => {
                    const v = e.target.value
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, year: v ? String(parseInt(v, 10)) : '' },
                    }))
                  }}
                  placeholder="e.g. 2015"
                  className="form-input"
                  min="1900"
                  max="2100"
                  disabled={contributionId && !canEdit}
                />
              </label>
            </div>
            <p className="form-hint" style={{ marginTop: '0.25rem' }}>At least one of Manufacturer, Model number, or Device type is required.</p>
          </div>
          <div className="contribution-form-section contribution-form-message-box">
            <label className="form-group">
              <span className="form-label">Short note / commit message (optional)</span>
              <input
                type="text"
                name="metadata.notes"
                value={formData.metadata.notes ?? ''}
                onChange={handleInputChange}
                placeholder="Brief message for the initial commit"
                className="form-input"
                disabled={contributionId && !canEdit}
              />
            </label>
          </div>
          <div className="contribution-form-section">
            <label className="form-group">
              <span className="form-label">Schematic files (optional)</span>
              <FileUpload
                onFilesChange={handleFilesChange}
                maxFiles={10}
                maxSizeMB={50}
                deferUpload={deferFileUpload}
                serverFiles={schematicServerFiles}
              />
              <span className="form-hint">Optional. Upload schematic or document files (e.g. PDF, images). You can add these later with a new commit.</span>
            </label>
            <label className="form-group">
              <span className="form-label">Circuit notes (optional)</span>
              <FileUpload
                onFilesChange={handleNoteFilesChange}
                maxFiles={5}
                maxSizeMB={50}
                markdownNotesOnly
                deferUpload={deferFileUpload}
                serverFiles={noteServerFiles}
              />
              <span className="form-hint">Optional. Markdown (.md) files under notes/. When submitting, include at least one schematic or one circuit notes file (circuit-notes-only repos are allowed).</span>
            </label>
            <label className="form-group">
              <span className="form-label">Source files (optional)</span>
              <FileUpload
                onFilesChange={handleSourceFilesChange}
                maxFiles={10}
                maxSizeMB={50}
                sourceFilesOnly
                withDescriptions
                descriptions={sourceFileDescriptions}
                onDescriptionChange={handleSourceDescriptionChange}
                deferUpload={deferFileUpload}
                serverFiles={sourceServerFiles}
              />
              <span className="form-hint">
                Optional. Stored under <code>source/</code> in the repo.{' '}
                <strong>KiCad files (.kicad_sch, .kicad_pro, etc.) are the currently official supported source package;</strong>{' '}
                more supported source types are on the way.
              </span>
            </label>
          </div>
        </>
      ) : (
        <>
          <div className="contribution-form-section">
            <label className="form-group">
              <span className="form-label">Submission Type</span>
              <select
                name="submission_type"
                value={formData.submission_type}
                onChange={handleInputChange}
                className="form-input"
                disabled={contributionId && !canEdit}
              >
                <option value="new_commit">New Commit</option>
                <option value="new_issue">New Issue</option>
                <option value="circuit_notes">Circuit notes only</option>
              </select>
            </label>

            {formData.repository_id ? (
              <div className="form-info" style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '6px', marginBottom: '1rem' }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                  Contributing to repository: <strong style={{ color: 'var(--text-primary)' }}>{formData.repository_id}</strong>
                </p>
              </div>
            ) : (
              <label className="form-group">
                <span className="form-label">Repository ID</span>
                <input
                  type="text"
                  name="repository_id"
                  value={formData.repository_id ?? ''}
                  onChange={handleInputChange}
                  placeholder="Enter repository ID or slug"
                  className="form-input"
                  disabled={contributionId && !canEdit}
                />
              </label>
            )}

            {formData.submission_type === 'new_issue' && (
              <>
                <div className="contribution-form-submit-note" style={{ marginBottom: '1rem' }} role="note">
                  Issues are notes to the repo owner and community to raise awareness—for example, potential fixes, corrections, bugs, or requests. No new files or documentation are required; just describe what you’d like to bring to their attention.
                </div>
                <label className="form-group">
                  <span className="form-label">Issue description *</span>
                  <textarea
                    name="metadata.notes"
                    value={formData.metadata.notes ?? ''}
                    onChange={handleInputChange}
                    placeholder="Describe the issue or request"
                    className="form-input form-textarea"
                    rows={3}
                    disabled={contributionId && !canEdit}
                  />
                </label>
              </>
            )}
          </div>

          {(formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes') && (
            <div className="contribution-form-section contribution-form-message-box">
              <label className="form-group">
                <span className="form-label">Commit message *</span>
                <input
                  type="text"
                  name="metadata.notes"
                  value={formData.metadata.notes ?? ''}
                  onChange={handleInputChange}
                  placeholder="Short message for this commit (required when submitting)"
                  className="form-input"
                  disabled={contributionId && !canEdit}
                />
                <span className="form-hint">Required when submitting for review or merging to main.</span>
              </label>
            </div>
          )}

          {(formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes') && (
            <div className="contribution-form-section">
              {formData.submission_type === 'new_commit' && (
                <div className="contribution-form-commit-type">
                  <span className="contribution-form-commit-type-label">How should this commit affect the repo?</span>
                  <select
                    name="metadata.commit_intent"
                    value={formData.metadata.commit_intent ?? 'add'}
                    onChange={handleInputChange}
                    className="contribution-form-commit-type-select"
                    disabled={contributionId && !canEdit}
                  >
                    <option value="add">Add or update files — keep existing files, add or replace by path</option>
                    <option value="replace">Replace repository — repo will contain only the files I upload</option>
                  </select>
                  <span className="contribution-form-commit-type-hint">
                    Add/update: your files are merged with the current repo. Replace: all other files are removed; only your uploads remain.
                  </span>
                </div>
              )}
              {formData.submission_type === 'circuit_notes' && (
                <div className="contribution-form-commit-type contribution-form-notes-intent">
                  <span className="contribution-form-commit-type-label">How should your note files affect the circuit notes?</span>
                  <select
                    name="metadata.notes_intent"
                    value={formData.metadata.notes_intent ?? 'add'}
                    onChange={handleInputChange}
                    className="contribution-form-commit-type-select"
                    disabled={contributionId && !canEdit}
                  >
                    <option value="add">Add to existing notes — my file(s) are added under notes/ (or replace same path)</option>
                    <option value="replace">Replace all notes — remove existing notes/ and use only my file(s)</option>
                  </select>
                  <span className="contribution-form-commit-type-hint">
                    Add: your files go under notes/ alongside existing ones. Replace: repo notes/ will contain only the files you upload.
                  </span>
                </div>
              )}
              <label className="form-group">
                <span className="form-label">
                  {formData.submission_type === 'new_commit' ? 'Schematic files (optional)' : 'Circuit notes *'}
                </span>
                <FileUpload
                  onFilesChange={handleFilesChange}
                  maxFiles={10}
                  maxSizeMB={50}
                  markdownNotesOnly={formData.submission_type === 'circuit_notes'}
                  deferUpload={deferFileUpload}
                  serverFiles={schematicServerFiles}
                />
                {formData.submission_type === 'circuit_notes' && (
                  <span className="form-hint">Markdown (.md) files are stored under notes/ in the repo. Add or replace (see above).</span>
                )}
                {formData.submission_type === 'new_commit' && (
                  <span className="form-hint">
                    Optional. Include at least one schematic, source, or circuit notes file when submitting (any combination).
                  </span>
                )}
              </label>
              {formData.submission_type === 'new_commit' && (
                <>
                  <label className="form-group">
                    <span className="form-label">Circuit notes (optional)</span>
                    <FileUpload
                      onFilesChange={handleNoteFilesChange}
                      maxFiles={5}
                      maxSizeMB={50}
                      markdownNotesOnly
                      deferUpload={deferFileUpload}
                      serverFiles={noteServerFiles}
                    />
                    <span className="form-hint">Optional. Add Markdown file(s) (.md) to the repo&apos;s circuit notes (notes/) with this commit. Adding to existing notes adds an additional file.</span>
                  </label>
                  <label className="form-group">
                    <span className="form-label">Source files (optional)</span>
                    <FileUpload
                      onFilesChange={handleSourceFilesChange}
                      maxFiles={10}
                      maxSizeMB={50}
                      sourceFilesOnly
                      withDescriptions
                      descriptions={sourceFileDescriptions}
                      onDescriptionChange={handleSourceDescriptionChange}
                      deferUpload={deferFileUpload}
                      serverFiles={sourceServerFiles}
                    />
                    <span className="form-hint">
                      Optional. Stored under <code>source/</code>; same path replaces the existing file.{' '}
                      <strong>KiCad files are the currently official supported source package;</strong>{' '}
                      more supported source types are on the way.
                    </span>
                  </label>
                </>
              )}
            </div>
          )}
          {/* Optional provenance metadata (encouraged for legal/quality clarity) */}
          {(formData.submission_type === 'new_commit' ||
            formData.submission_type === 'circuit_notes' ||
            isNewRepo) && (
            <details className="contribution-form-section" style={{ marginTop: '0.75rem' }}>
              <summary
                className="form-label"
                style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
              >
                Provenance (optional but encouraged)
              </summary>
              <p className="form-hint" style={{ marginBottom: '0.75rem' }}>
                Help other readers understand how this work was produced. This information may be
                shown on the repository and in commit history.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label className="form-group">
                  <span className="form-label">How was it produced?</span>
                  <input
                    type="text"
                    name="metadata.production_method"
                    value={formData.metadata.production_method ?? ''}
                    onChange={handleInputChange}
                    placeholder="e.g. measurement, teardown, PCB tracing, firmware disassembly"
                    className="form-input"
                    disabled={contributionId && !canEdit}
                  />
                </label>
                <label className="form-group">
                  <span className="form-label">Tools used</span>
                  <input
                    type="text"
                    name="metadata.tools_used"
                    value={formData.metadata.tools_used ?? ''}
                    onChange={handleInputChange}
                    placeholder="e.g. oscilloscope, DMM, logic analyzer"
                    className="form-input"
                    disabled={contributionId && !canEdit}
                  />
                </label>
                <label className="form-group">
                  <span className="form-label">Confidence level</span>
                  <input
                    type="text"
                    name="metadata.confidence_level"
                    value={formData.metadata.confidence_level ?? ''}
                    onChange={handleInputChange}
                    placeholder="e.g. high / medium / low, or a brief note"
                    className="form-input"
                    disabled={contributionId && !canEdit}
                  />
                </label>
                <label className="form-group">
                  <span className="form-label">Known differences from originals</span>
                  <textarea
                    name="metadata.known_differences"
                    value={formData.metadata.known_differences ?? ''}
                    onChange={handleInputChange}
                    placeholder="Corrections, simplifications, or intentional modifications"
                    className="form-input form-textarea"
                    rows={2}
                    disabled={contributionId && !canEdit}
                  />
                </label>
              </div>
            </details>
          )}
        </>
      )}

      <div className="contribution-form-actions">
        {(formData.submission_type === 'new_commit' ||
          formData.submission_type === 'circuit_notes' ||
          isNewRepo) && (
          <div
            className="contribution-form-section"
            style={{ marginBottom: '0.75rem' }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
              }}
            >
              <input
                type="checkbox"
                checked={hasLegalAcknowledgment}
                onChange={(e) => setHasLegalAcknowledgment(e.target.checked)}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                By submitting, I confirm that this upload is my original work or
                derived from lawful reverse engineering, and that I am not
                uploading scans, photos, or transcriptions of copyrighted
                manuals, traced schematics from proprietary documents, or files
                labeled &quot;official,&quot; &quot;factory,&quot; or &quot;service
                manual.&quot;
              </span>
            </label>
          </div>
        )}
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isLoading || isSaving}
          >
            Cancel
          </button>
        )}
        {formData.status === 'draft' && (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSaveDraft}
              disabled={isLoading || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmitWithBranchForkConfirm}
              disabled={isLoading || isSaving}
            >
              {isLoading
                ? (isRepoOwner && (formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes') && formData.repository_id ? 'Merging...' : 'Submitting...')
                : (isRepoOwner && (formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes') && formData.repository_id ? 'Merge to main' : 'Submit for Review')}
            </button>
            {(formData.submission_type === 'new_commit' || formData.submission_type === 'circuit_notes') && !isNewRepo && (
              <div
                className="contribution-form-submit-note"
                role="note"
                aria-label="How submission works"
                style={{
                  marginTop: '0.5rem',
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                }}
              >
                <strong style={{ color: 'var(--accent)' }}>Admin path:</strong>{' '}
                Submitting as the repo admin will merge your changes directly to <strong>main</strong> (the canonical branch).{' '}
                <strong style={{ color: 'var(--accent)' }}>Contributor path:</strong>{' '}
                Submitting as a non-admin will create a temporary <strong>contribution branch</strong> and a{' '}
                <strong>pull request</strong> that repo admins can review and merge into main.
              </div>
            )}
          </>
        )}
        {isPendingBranchUpdate && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isLoading || isSaving}
          >
            {isLoading ? 'Updating...' : 'Update branch'}
          </button>
        )}
      </div>
      {showBranchForkConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
          }}
          onClick={() => setShowBranchForkConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary)',
              padding: '1.75rem',
              borderRadius: '8px',
              maxWidth: '520px',
              width: '90%',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: '0.75rem',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              Branch &amp; fork guidelines
            </h3>
            <p
              style={{
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: 'var(--text-secondary)',
                marginBottom: '1.25rem',
              }}
            >
              <strong>Important:</strong> If your contributions are not being approved, you should{' '}
              <strong>fork the repository</strong> and work in your own copy instead of repeatedly opening new contribution
              branches. Spamming unapproved branches instead of forking may result in{' '}
              <strong>account suspension</strong>.
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                marginTop: '0.5rem',
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowBranchForkConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setHasAcknowledgedBranchFork(true)
                  setShowBranchForkConfirm(false)
                  handleSubmit()
                }}
              >
                I understand and agree
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContributionForm
